"use client"

import { ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"
import { motion } from "framer-motion"
import { useState } from "react"
import { cn } from "@/lib/utils"
import { useLocalizedPath } from "@/lib/locale"
import { useLanguage } from "@/contexts/LanguageContext"
import { SeoImage } from "./SeoImage"

interface PortfolioGalleryProps {
  title?: string;
  eyebrow?: string;
  archiveButton?: {
    text: string;
    href: string;
  };
  images?: Array<{
    src: string;
    alt: string;
    title?: string;
    width?: number;
    height?: number;
  }>;
  className?: string;
  maxHeight?: number;
  spacing?: string;
  onImageClick?: (index: number) => void;
  /**
   * Whether to pause marquee animation on hover (mobile only)
   * @default true
   */
  pauseOnHover?: boolean;
  /**
   * Number of times to repeat the content in marquee (mobile only)
   * @default 4
   */
  marqueeRepeat?: number;
  /**
   * Whether to use compact spacing (reduces padding/margins)
   * @default false
   */
  compact?: boolean;
}

export function PortfolioGallery({
  title,
  eyebrow,
  archiveButton,
  images: customImages,
  className = "",
  maxHeight = 120,
  spacing = "-space-x-72 md:-space-x-80",
  onImageClick,
  pauseOnHover = true,
  marqueeRepeat = 4,
  compact = false
}: PortfolioGalleryProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const localizedPath = useLocalizedPath()
  const { language } = useLanguage()

  const resolvedTitle =
    title ??
    (language === "en" ? "Successful Projects" : "Επιτυχημένα Έργα")
  const resolvedArchiveButton =
    archiveButton ?? {
      text: language === "en" ? "View the portfolio" : "Δείτε το Portfolio",
      href: "/projects",
    }
  const description =
    language === "en"
      ? "Results that speak for themselves."
      : "Αποτελέσματα που μιλούν από μόνα τους."
  
  const defaultImages = [
    { src: "/portfolio-screenshots/Personal CV & Portfolio Design (UK).png", alt: "Personal CV & Portfolio (UK)", width: 1280, height: 720 },
    { src: "/portfolio-screenshots/Web Design & Development for VoltBuild (GR).png", alt: "VoltBuild (GR)", width: 1280, height: 720 },
    { src: "/portfolio-screenshots/Web Design for Carpenter (UK).png", alt: "Carpenter (UK)", width: 1280, height: 720 },
    { src: "/portfolio-screenshots/Web Design for Construction Company (UK).png", alt: "Construction Company (UK)", width: 1280, height: 720 },
    { src: "/portfolio-screenshots/Web Design for Pixelpro Solutions (UK).png", alt: "Pixelpro Solutions (UK)", width: 1280, height: 720 },
    { src: "/portfolio-screenshots/Web Design for Underground Talent (UK).png", alt: "Underground Talent (UK)", width: 1280, height: 720 },
    { src: "/portfolio-screenshots/Web Design for a Photographer Studio (GR).png", alt: "Photographer Studio (GR)", width: 1280, height: 720 },
    { src: "/portfolio-screenshots/Web Development - SaaS Application Development (UK) .png", alt: "SaaS Application (UK)", width: 1280, height: 720 },
    { src: "/portfolio-screenshots/Web Development for Trade Signals Community (GR).png", alt: "Trade Signals Community (GR)", width: 1280, height: 720 },
    { src: "/portfolio-screenshots/eCommerce Design - Dynamic Website Development for Pieria Strom (GR).png", alt: "Pieria Strom (GR)", width: 1280, height: 720 },
  ]
  
  const images = customImages || defaultImages

  return (
    <section
      aria-label={resolvedTitle}
      className={`relative ${compact ? 'py-1 md:py-2' : 'py-16 md:py-24'} px-4 ${className}`}
      id="portfolio"
    >
      <div className="container mx-auto">
        <div className="max-w-7xl mx-auto bg-black/30 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
          {/* Header Section */}
          <div className="relative z-10 text-center pt-16 pb-8 px-8">
            {eyebrow ? (
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-highlight/80 mb-4">
                {eyebrow}
              </p>
            ) : null}
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 text-balance">{resolvedTitle}</h2>
            <p className="text-gray-300/80 text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
              {description}
            </p>

            <Link
              to={localizedPath(resolvedArchiveButton.href)}
              className="inline-flex items-center gap-3 bg-white text-black px-6 py-3 rounded-full font-medium hover:bg-gray-200 transition-colors group mb-10"
            >
              <span>{resolvedArchiveButton.text}</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Desktop 3D overlapping layout - hidden on mobile */}
          <div className="hidden md:block relative overflow-hidden h-[400px] -mb-[200px]">
            <div className={`flex ${spacing} pb-8 pt-40 items-end justify-center`}>
              {images.map((image, index) => {
                // Calculate stagger height - peak in middle, descending to edges
                const totalImages = images.length
                const middle = Math.floor(totalImages / 2)
                const distanceFromMiddle = Math.abs(index - middle)
                const staggerOffset = maxHeight - distanceFromMiddle * 20

                const zIndex = totalImages - index

                const isHovered = hoveredIndex === index
                const isOtherHovered = hoveredIndex !== null && hoveredIndex !== index

                // When hovering: hovered card moves to consistent top position, others move to baseline
                const yOffset = isHovered ? -120 : isOtherHovered ? 0 : -staggerOffset

                return (
                  <motion.div
                    key={index}
                    className="group cursor-pointer flex-shrink-0"
                    style={{
                      zIndex: zIndex,
                    }}
                    initial={{
                      transform: `perspective(5000px) rotateY(-45deg) translateY(200px)`,
                      opacity: 0,
                    }}
                    animate={{
                      transform: `perspective(5000px) rotateY(-45deg) translateY(${yOffset}px)`,
                      opacity: 1,
                    }}
                    transition={{
                      duration: 0.2, // Much faster hover animation
                      delay: index * 0.05, // Faster entrance stagger
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                    onHoverStart={() => setHoveredIndex(index)}
                    onHoverEnd={() => setHoveredIndex(null)}
                    onClick={() => onImageClick?.(index)}
                  >
                    <div
                      className="relative aspect-video w-64 md:w-80 lg:w-96 rounded-lg overflow-hidden transition-transform duration-300 group-hover:scale-105"
                      style={{
                        boxShadow: `
                          rgba(0, 0, 0, 0.01) 0.796192px 0px 0.796192px 0px,
                          rgba(0, 0, 0, 0.03) 2.41451px 0px 2.41451px 0px,
                          rgba(0, 0, 0, 0.08) 6.38265px 0px 6.38265px 0px,
                          rgba(0, 0, 0, 0.25) 20px 0px 20px 0px
                        `,
                      }}
                    >
                      <SeoImage
                        src={image.src || "/placeholder.svg"}
                        alt={image.alt}
                        width={image.width || 1280}
                        height={image.height || 720}
                        loading={index < 2 ? "eager" : "lazy"}
                        priority={index < 2}
                        className="w-full h-full object-cover object-left-top"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      />
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>

          {/* Mobile marquee layout */}
          <div className="block md:hidden relative pb-8">
            <div
              className={cn(
                "group flex overflow-hidden p-2 [--duration:40s] [--gap:1rem] [gap:var(--gap)]",
                "flex-row"
              )}
            >
              {Array(marqueeRepeat)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={i}
                    className={cn(
                      "flex shrink-0 justify-around [gap:var(--gap)]",
                      "animate-marquee flex-row",
                      {
                        "group-hover:[animation-play-state:paused]": pauseOnHover,
                      }
                    )}
                  >
                    {images.map((image, index) => (
                      <div
                        key={`${i}-${index}`}
                        className="group cursor-pointer flex-shrink-0"
                        onClick={() => onImageClick?.(index)}
                      >
                        <div
                          className="relative aspect-video w-64 rounded-lg overflow-hidden transition-transform duration-300 group-hover:scale-105"
                          style={{
                            boxShadow: `
                              rgba(0, 0, 0, 0.01) 0.796192px 0px 0.796192px 0px,
                              rgba(0, 0, 0, 0.03) 2.41451px 0px 2.41451px 0px,
                              rgba(0, 0, 0, 0.08) 6.38265px 0px 6.38265px 0px,
                              rgba(0, 0, 0, 0.25) 20px 0px 20px 0px
                            `,
                          }}
                        >
                          <SeoImage
                            src={image.src || "/placeholder.svg"}
                            alt={image.alt}
                            width={image.width || 1280}
                            height={image.height || 720}
                            loading="lazy"
                            className="w-full h-full object-cover object-left-top"
                            sizes="(max-width: 768px) 100vw, 50vw"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
